import React, { useContext } from 'react'
import dayjs from 'dayjs'
import { FaInfoCircle, FaAsterisk, FaTimes } from 'react-icons/fa'
import { useLocation } from 'react-router-dom'

import urls from '../constants/urls'
import { AppStateContext } from '../../components/features/explorer/AppStateContext'
import { getFieldNameFromColumn } from '../stringUtils'
import Tooltip from '../../components/elem/Tooltip'
import {
    uploadInputSelector,
    uploadValidate,
    FieldComponent,
    getInputType,
} from '../form/formFieldParser'
import cloneDeep from 'lodash.clonedeep'
import { convertFieldDuplicateString } from '../submissions/values'
import { getSortType } from './sortBy'

const getDescriptionValue = (value, filterItem) => {
    const matchingValueObject = value
        ? filterItem.Values.find(
              x =>
                  x.code === value.toString() ||
                  x.code === value ||
                  x.code === parseInt(value)
          )
        : null
    if (matchingValueObject) {
        return matchingValueObject.codedescription
    } else {
        return value
    }
}

const validateDate = (date, format) => {
    const formatted = dayjs(date, format).format(format)
    return formatted === date
}

const roundToNDigits = (number, n) => {
    return +number.toFixed(n)
}

const formatValue = (value, options) => {
    // try to cast value as date
    let v = value
    try {
        const isDate = validateDate(value, 'YYYY-MM-DDTHH:mm:ss')
        if (isDate) {
            v = dayjs(value)
                .format('YYYY/MM/DD')
                .toString()
        }

        // if the value is a number, and roundIfApplicable,
        // round it to one digits
        if (options.roundIfApplicable) {
            v = roundToNDigits(value, 1)
        }

        if (options.separator) {
            const splits = value
                .split(options.separator)
                .map(x => x && x.trim() !== '' ? `<li> ${x.trim()}</li>` : null)
            v = `<ul class='dashed'>${splits.join('')}</ul>`
        }
        return v
    } catch {
        // if caught, its not a date value
        return v
    }
}

const WellLinkCell = ({ cell, filterFields, idColumn }) => {
    const location = useLocation()
    const context = useContext(AppStateContext)
    if (!(location.pathname === urls.home)) {
        return <DescriptionCell cell={cell} filterFields={filterFields} />
    }
    const { updateDetailState } = context
    const idValue = idColumn && cell.row.getValue(idColumn) ? cell.row.getValue(idColumn) : cell.getValue()

    return (
        <div className="descriptionCell linkCell">
            <span
                className=""
                onClick={e => {
                    updateDetailState('well', {
                        visible: true,
                        facilityID: idValue,
                    })
                    e.stopPropagation()
                }}
            >
                {formatValue(cell.getValue())}
            </span>
        </div>
    )
}

const DescriptionCell = ({ cell, filterFields }) => {
    const controlName = cell.column.id
    const initialValue = cell.getValue()
    const filterItem = filterFields.find(x => x.ControlName === controlName)
    const hasFilterFields = filterItem && !!filterItem.Values
    const value = hasFilterFields
        ? getDescriptionValue(initialValue, filterItem)
        : initialValue
    return <div className="descriptionCell">{formatValue(value)}</div>
}

const HeaderCell = ({ width, columnName, filterFields, indicateRequired }) => {
    let filterItem = filterFields.find(x => x.ColumnName === columnName || x.ControlName === columnName)
    const hasFilterInfo = filterItem && !!filterItem.ColumnDescription
    const required = filterItem && indicateRequired && !!filterItem.Required
    return (
        <div className="headerCell" style={width ? {width: width} : null}>
            <div className="headerCellText">
                <span>
                    {getFieldNameFromColumn(columnName, filterFields)}
                </span>
                {required ? (
                    <span className="requiredStar has-text-danger">
                        {<FaAsterisk />}
                    </span>
                ) : null}{' '}
            </div>
            {hasFilterInfo ? (
                <div className="headerCellIcon">
                    <span
                        data-tooltip-content={`${filterItem.ColumnDescription}`}
                        data-tooltip-id={`table-${columnName}`}
                        className="icon is-left"
                    >
                        <FaInfoCircle />
                        <Tooltip id={`table-${columnName}`} />
                    </span>
                </div>
            ) : null}
        </div>
    )
}

const parseFormColumns = (data, filterFields, linkColumn, idColumn) => {
    if (data.length) {
        const exampleData = data[0]
        // get the columns that are not hidden with colDevControls table
        const visibleFields = filterFields
            .filter(x => x.DisplayInTable)
            .map(x => x.ControlName)
        const columns = Object.keys(exampleData)
            .filter(fieldName => {
                const fieldFilterItem = filterFields.find(
                    x => x.ColumnName === fieldName
                )
                return (
                    fieldFilterItem &&
                    visibleFields.includes(fieldFilterItem.ControlName)
                )
            })
            .reduce((acc, cur) => {
                return acc.concat([
                    {
                        header: () => (
                            <HeaderCell
                                columnName={cur}
                                filterFields={filterFields}
                            />
                        ),
                        accessorKey: cur,
                        cell: info =>
                            info.column.id === linkColumn ? (
                                <WellLinkCell
                                    cell={info}
                                    idColumn={idColumn}
                                    filterFields={filterFields}
                                />
                            ) : (
                                <DescriptionCell
                                    cell={info}
                                    filterFields={filterFields}
                                />
                            ),
                        sortingFn: getSortType(cur, filterFields)
                    },
                ])
            }, [])
        return columns
    }
    return []
}

const parseUploadColumns = (
    data,
    uploadConfig,
    submissionState,
    viewOnly,
    setDisplayDeleteModal
) => {
    // get the columns that are not hidden with colDevControls table
    let configsWithDependentFields = uploadConfig
    let returnColumns = []
    const visibleFields = uploadConfig
        .filter(x => x.DisplayInTable)
        .map(x => x.ControlName)
    const columns = visibleFields.reduce((acc, cur, idx) => {
        const uploadField = cloneDeep(uploadConfig.find(x => x.ControlName === cur))
        return acc.concat([
            {
                header: () => (
                    <HeaderCell width={uploadField && uploadField.ColumnWidth ? uploadField.ColumnWidth : 175} columnName={uploadField.ColumnName} filterFields={uploadConfig} indicateRequired={true} />
                ),
                accessorKey: cur,
                cell: ({ cell, row, table }) => {
                    if (viewOnly) {
                        uploadField.ViewOnly = true
                    }
                    const fieldProps = {
                        field: uploadField,
                        formInputSelector: uploadInputSelector,
                        getInputType,
                        formWidth: '',
                        hrColorClass: '',
                        formName: null,
                        validate: uploadValidate,
                        idx,
                        rowIdx: cell.row.index,
                        displayLabel: false,
                        rowData: data,
                    }
                    return <FieldComponent {...fieldProps} />
                },
            },
        ])
    }, [])
    returnColumns = [...columns]

    if (uploadConfig && uploadConfig.length) {
        const exampleConfig = uploadConfig[0]
        const requires = exampleConfig.Requires
        if (requires && submissionState[requires] && Array.isArray(submissionState[requires])) {
            const tableName = requires[0].toUpperCase() +
            requires.slice(1)
            const columnTitle = `${tableName} Name`
            const valueAccessor = `Upload${tableName}ID`
            const descriptionAccessor = `${tableName}Name`
            const fieldDuplicateAccessor = `IsFieldDuplicate`
            const values = submissionState[requires].length
                ? submissionState[requires].map(x => ({
                      code: x[valueAccessor],
                      codedescription: `${x[descriptionAccessor]}${!!x[fieldDuplicateAccessor] ? convertFieldDuplicateString(x[fieldDuplicateAccessor]) : ''}`,
                      active: true
                  }))
                : null

            const field = {
                ...exampleConfig,
                ColumnDescription: null,
                Description: null,
                ColumnName: valueAccessor,
                ColumnTitle: columnTitle,
                CompareItems: '=',
                ControlName: valueAccessor,
                ControlType: 'Select',
                DataType: 'Int',
                MaxLength: null,
                MaxValue: null,
                MinValue: null,
                Prompt: `${columnTitle}:`,
                Required: true,
                Requires: requires,
                SectionType: 'Table',
                Values: values,
                ViewOnly: viewOnly
            }
            configsWithDependentFields = [...configsWithDependentFields, field]
            const dependentIdColumn = {
                header: () => (
                    <HeaderCell width={175} columnName={valueAccessor} filterFields={[field]} indicateRequired={true} />
                ),
                accessorKey: valueAccessor,
                cell: ({ cell, row, table }) => {
                    const fieldProps = {
                        field,
                        formInputSelector: uploadInputSelector,
                        getInputType,
                        formWidth: '',
                        hrColorClass: '',
                        formName: null,
                        validate: uploadValidate,
                        rowIdx: cell.row.index,
                        displayLabel: false,
                        rowData: data,
                    }
                    return <FieldComponent {...fieldProps} />
                },
            }

            returnColumns = [dependentIdColumn, ...returnColumns]
        }
    }
    
    const deleteColumn = {
        header: () => <HeaderCell columnName={''} filterFields={[]} />,
        accessorKey: 'idx',
        cell: (props) => {
            return (
            <div>
                <div
                    onClick={() => setDisplayDeleteModal({display: true, tableProps: props, sectionConfig: uploadConfig })}
                    className="link padding-top-sm"
                >
                    <FaTimes />
                </div>
            </div>
        )},
    }
    if (!viewOnly) {
        returnColumns = [...returnColumns, deleteColumn]      
    }

    return { returnColumns, configsWithDependentFields }
}

export default parseFormColumns
export { getDescriptionValue, formatValue, parseUploadColumns }
